import { fetchChainTime } from "../services/api.js";

/**
 * THE single source of chain time on the frontend.
 *
 * Every time-sensitive value the UI shows or computes — loan/deposit
 * interest accrual, maturity countdowns, lifecycle state (Active →
 * Matured → Dormant → Decay) — must key off CHAIN time, never the
 * browser wall clock. The two agree on a public chain, but diverge by
 * weeks/months under local time-warp testing (gilder-solidity/scripts/
 * timeWarp.js), which froze / mis-counted interest.
 *
 * Rather than each surface reading the chain its own way, this module
 * holds ONE offset between chain time and the browser wall clock, kept
 * fresh from the backend's authoritative `getChainTimeMs()` (exposed at
 * GET /api/chain/time). Pure utilities (format.js) read it via the
 * non-hook accessors below; React components tick off the `useChainTime`
 * hook, which is a thin wrapper over the same offset.
 *
 *   chain now = wall now + offset
 */

// Offset in SECONDS: chainBlockTimestamp − wallClockSeconds. Module-global
// so a pure function and a dozen components share one value.
let offsetSec = 0;
let hasOffset = false;
let started = false;

// Persist the offset across full page reloads. Without this, every reload
// resets `offsetSec` to 0 and the accessors briefly return wall-clock time
// until the async GET /api/chain/time resolves. On a time-warped chain that
// window makes chain "now" fall BEFORE each position's chain startTime, so
// every live interest figure flashes $0 and then jumps to its true value a
// second later ("resets to 0 and climbs back up" on reload). Seeding from
// the last known offset makes the very first render already chain-accurate;
// the background sync still refines any drift on the next tick.
const OFFSET_KEY = "gilder.chainOffsetSec";

(function seedOffsetFromStorage() {
    try {
        const raw = localStorage.getItem(OFFSET_KEY);
        if (raw == null) return;
        const persisted = Number(raw);
        if (Number.isFinite(persisted)) {
            offsetSec = persisted;
            hasOffset = true;
        }
    } catch {
        /* storage unavailable (private mode / SSR) — fall back to offset 0 */
    }
})();

/** Updates the shared offset from an absolute chain time in ms. */
export function setChainTimeMs(chainMs) {
    if (!Number.isFinite(chainMs) || chainMs <= 0) return;
    offsetSec = Math.round(chainMs / 1000) - Math.floor(Date.now() / 1000);
    hasOffset = true;
    try {
        localStorage.setItem(OFFSET_KEY, String(offsetSec));
    } catch {
        /* storage unavailable — the in-memory offset still works this session */
    }
}

/** Offset (seconds) between chain time and the browser wall clock. */
export function getChainOffsetSec() {
    return offsetSec;
}

/** True once a real chain time has been observed at least once. */
export function chainOffsetAvailable() {
    return hasOffset;
}

/** Current chain time in milliseconds (wall now + offset). */
export function chainNowMs() {
    return Date.now() + offsetSec * 1000;
}

/** Current chain time in whole seconds (matches block.timestamp units). */
export function chainNowSec() {
    return Math.floor(Date.now() / 1000) + offsetSec;
}

/**
 * Starts the background sync that keeps the offset fresh from the backend.
 * Idempotent — safe to call from multiple components / app bootstrap; only
 * the first call installs the poller. Returns immediately; the first sync
 * runs on the next tick. Until it resolves, the accessors return wall-clock
 * values (offset 0), which is correct on a non-warped chain.
 */
export function startChainClock({ pollMs = 30_000 } = {}) {
    if (started) return;
    started = true;
    const sync = async () => {
        try {
            const data = await fetchChainTime();
            if (data && data.chainTimeMs) setChainTimeMs(Number(data.chainTimeMs));
        } catch {
            /* keep the last good offset — never throw from the clock */
        }
    };
    sync();
    if (pollMs) setInterval(sync, pollMs);
}
